import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { mloPhotoUrl } from "@/lib/photo";
import { Toaster } from "sonner";

import Nav from "@/components/landing/Nav";
import Hero from "@/components/landing/Hero";
import WhyCheckRate from "@/components/landing/WhyCheckRate";
import HowItWorks from "@/components/landing/HowItWorks";
import MeetJason from "@/components/landing/MeetJason";
import FAQ from "@/components/landing/FAQ";
import PrivacyTrust from "@/components/landing/PrivacyTrust";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import { MLOContext } from "@/lib/mloContext";
import { track, EVENTS } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MLOLanding() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, mlo: null, notFound: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/mlos/${encodeURIComponent(slug)}/public`);
        if (cancelled) return;
        const photoUrl = mloPhotoUrl(res.data);
        // Secondary photo shown on "Meet …" section; falls back to primary if unset
        const photoSecondaryUrl = mloPhotoUrl(res.data, "secondary");
        setState({
          loading: false,
          mlo: { ...res.data, photoUrl, photoSecondaryUrl },
          notFound: false,
        });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, mlo: null, notFound: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (state.mlo) {
      document.title = `${state.mlo.name} · Great Rate Check`;
      track(EVENTS.MLO_LANDING_VIEW, { mlo_slug: state.mlo.slug, mlo_name: state.mlo.name });
    }
  }, [state.mlo]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center text-navy">
        <p className="text-sm text-ink-500">Loading…</p>
      </div>
    );
  }

  if (state.notFound) {
    return <Navigate to="/" replace />;
  }

  return (
    <MLOContext.Provider value={state.mlo}>
      <div data-testid={`mlo-landing-${state.mlo.slug}`} className="min-h-screen bg-linen text-navy antialiased">
        <Nav />
        <main>
          <Hero />
          <WhyCheckRate />
          <HowItWorks />
          <MeetJason />
          <FAQ />
          <PrivacyTrust />
          <FinalCTA />
        </main>
        <Footer />
        <Toaster position="top-center" richColors toastOptions={{ className: "font-body" }} />
      </div>
    </MLOContext.Provider>
  );
}
